import React from 'react';

interface PremiumSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PremiumSuccessModal: React.FC<PremiumSuccessModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div 
        className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-cyan-500/30"
        style={{
          boxShadow: '0 0 60px rgba(6, 182, 212, 0.3), 0 0 100px rgba(6, 182, 212, 0.1)'
        }}
      >
        {/* Success Icon with Animation */}
        <div className="flex justify-center mb-6">
          <div 
            className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center"
            style={{
              animation: 'pulse 2s ease-in-out infinite',
              boxShadow: '0 0 30px rgba(6, 182, 212, 0.5)'
            }}
          >
            <svg 
              className="w-10 h-10 text-white" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              style={{ animation: 'checkmark 0.5s ease-out forwards' }}
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={3} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center mb-2">
          <span className="bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
            Welcome to Premium!
          </span>
        </h2>

        {/* Subtitle */}
        <p className="text-gray-300 text-center mb-6">
          Payment successful. Your subscription is now active.
        </p>

        {/* Features unlocked */}
        <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700/50">
          <p className="text-sm text-gray-400 text-center mb-3">You now have access to:</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-cyan-400">
              <span className="text-cyan-500">✓</span> Cloud Sync
            </div>
            <div className="flex items-center gap-2 text-cyan-400">
              <span className="text-cyan-500">✓</span> Unlimited Setlists
            </div>
            <div className="flex items-center gap-2 text-cyan-400">
              <span className="text-cyan-500">✓</span> PDF Export
            </div>
            <div className="flex items-center gap-2 text-cyan-400">
              <span className="text-cyan-500">✓</span> Jam Sessions
            </div>
            <div className="flex items-center gap-2 text-cyan-400">
              <span className="text-cyan-500">✓</span> Edit Annotations
            </div>
            <div className="flex items-center gap-2 text-cyan-400">
              <span className="text-cyan-500">✓</span> Priority Support
            </div>
          </div>
        </div>

        {/* OK Button */}
        <button
          onClick={onClose}
          className="w-full py-3 px-6 rounded-xl font-semibold text-white
                   bg-gradient-to-r from-cyan-600 to-cyan-500 
                   hover:from-cyan-500 hover:to-cyan-400
                   transform hover:scale-[1.02] active:scale-[0.98]
                   transition-all duration-200
                   shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
        >
          Start Using Premium
        </button>

        {/* Chord Notator branding */}
        <div className="mt-4 flex justify-center items-center gap-2 opacity-60">
          <div className="flex gap-0.5">
            <div className="w-3 h-3 bg-cyan-500 rounded-sm transform rotate-12"></div>
            <div className="w-3 h-3 bg-cyan-400 rounded-sm transform -rotate-12"></div>
          </div>
          <span className="text-xs text-gray-500 font-medium tracking-wide">CHORD NOTATOR</span>
        </div>
      </div>

      {/* CSS Animation for checkmark */}
      <style>{`
        @keyframes checkmark {
          0% {
            stroke-dasharray: 100;
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 30px rgba(6, 182, 212, 0.5);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 50px rgba(6, 182, 212, 0.7);
          }
        }
      `}</style>
    </div>
  );
};

export default PremiumSuccessModal;
